import React, { useMemo, useState } from 'react';
import { useScopedTranslation } from '../../../application/localization/useScopedTranslation.ts';
import { Breadcrumbs } from '../../../components/uikit/Breadcrumbs.tsx';
import styles from './AccountSettings.module.scss';
import clsx from 'clsx';
import {
  logOut,
  useIsAuthorized,
} from '../../../helpers/auth/auth-interceptor.ts';
import { getCountries } from '../../../helpers/countries-helper.ts';
import {
  Button,
  ButtonColor,
} from '../../../components/uikit/buttons/Button.tsx';
import LogoutIcon from 'assets/icons/logout.svg?react';
import { ActionText } from '../../../components/uikit/ActionText.tsx';
import {
  type UserOptionsDto,
  UserRole,
} from '../../../services/api/api-client.types.ts';
import { UserMenuCategory } from '../rootPage/RootPage.tsx';
import { useAnalytics } from '../../../helpers/analytics.ts';
import { ChangePasswordModal } from './components/ChangePasswordModal.tsx';
import {
  LoadingPage,
  LoadingType,
} from '../../../components/animations/LoadingPage.tsx';
import { queryClient } from '../../../services/api/query-client-helper.ts';
import Logger from 'js-logger';
import { Toggle } from '../../../components/uikit/Toggle.tsx';
import { AllowedRoles } from '../../../application/constants/allowed-roles.ts';
import {
  CmsPageQuery,
  UserClient,
  UserQuery,
} from 'services/api/api-client.ts';
import NoNotificationsImage from 'assets/img/no-notifications.svg?react';
import { Loading } from '../../../components/uikit/suspense/Loading.tsx';

const firmware = 'firmware';
type NotificationOptionKey =
  | 'isFirmwareNotificationsDisabled'
  | 'isQualificationNotificationsDisabled'
  | 'isRepairsNotificationsDisabled';

export const AccountSettings = () => {
  const i18n = useScopedTranslation('Page.AccountSettingsPage');

  const [isChangePassModalShown, setIsChangePassModalShown] = useState(false);
  const [loadingStatus, setlLoadingStatus] = useState<LoadingType>('none');

  const changeLoadingStatus = (status: LoadingType) => {
    setlLoadingStatus(status);
  };

  const { trackEvent } = useAnalytics();

  const isAuth = useIsAuthorized() === true;
  const userQuery = UserQuery.useGetCurrentUserInfoQuery({
    enabled: isAuth,
  });

  const isAdmin =
    userQuery.data?.claimRoles.includes(UserRole.Admin) ||
    userQuery.data?.claimRoles.includes(UserRole.SemiAdmin);

  const inaccessibleUrlsQuery =
    CmsPageQuery.useGetInaccessiblePublishedUrlsQuery({
      retry: false,
      throwOnError: false,
      enabled: !isAdmin,
      staleTime: 3 * 60 * 1000,
    });

  const language = i18n.i18n.language;

  const countryOptions = useMemo(
    () => getCountries(language.split('-')[0]),
    [language],
  );

  const country = countryOptions.find(
    (o) => o.value === userQuery.data?.country,
  );

  const handleLogout = async () => {
    trackEvent({
      category: UserMenuCategory,
      action: 'Click logout',
      name: 'Click logout',
    });

    try {
      await logOut();
      queryClient.clear();
    } catch (e) {
      Logger.error(e);
    }
  };

  const isQualificationVisible = !!userQuery.data?.claimRoles.some((role) =>
    AllowedRoles.QualificationsPage.includes(role),
  );

  const isRepairsVisible = !!userQuery.data?.claimRoles.some((role) =>
    AllowedRoles.RepairsPage.includes(role),
  );

  const isFirmwareVisible =
    (!inaccessibleUrlsQuery.data?.some(
      (inaccessibleUrl) => inaccessibleUrl === firmware,
    ) &&
      inaccessibleUrlsQuery.isFetched) ||
    isAdmin;

  // it's temporary, we will adjust firmware notifications
  // const isNotificationsHidden =
  //   !isQualificationVisible && !isFirmwareVisible && !isRepairsVisible;
  const isNotificationsHidden = !isQualificationVisible && !isRepairsVisible;

  const isFirmwareNotificationsDisabled =
    !!userQuery.data?.options?.isFirmwareNotificationsDisabled;
  const isQualificationNotificationsDisabled =
    !!userQuery.data?.options?.isQualificationNotificationsDisabled;
  const isRepairsNotificationsDisabled =
    !!userQuery.data?.options?.isRepairsNotificationsDisabled;

  const updateNotificationOption = async (
    option: NotificationOptionKey,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const previousOptions = userQuery.data?.options;
    const updatedOptionValue = !event.target.checked;

    const nextOptions: UserOptionsDto = {
      lastReadNotificationDate:
        previousOptions?.lastReadNotificationDate ?? null,
      isFirmwareNotificationsDisabled:
        option === 'isFirmwareNotificationsDisabled'
          ? updatedOptionValue
          : previousOptions?.isFirmwareNotificationsDisabled ?? null,
      isQualificationNotificationsDisabled:
        option === 'isQualificationNotificationsDisabled'
          ? updatedOptionValue
          : previousOptions?.isQualificationNotificationsDisabled ?? null,
      isRepairsNotificationsDisabled:
        option === 'isRepairsNotificationsDisabled'
          ? updatedOptionValue
          : previousOptions?.isRepairsNotificationsDisabled ?? null,
    };

    await UserClient.updateCurrentUserInfo({
      options: nextOptions,
    });

    void userQuery.refetch();
  };

  const handleFirmwareToggleChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    await updateNotificationOption('isFirmwareNotificationsDisabled', event);
  };

  const handleQualificationsToggleChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    await updateNotificationOption(
      'isQualificationNotificationsDisabled',
      event,
    );
  };

  const handleRepairsToggleChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    await updateNotificationOption('isRepairsNotificationsDisabled', event);
  };

  return (
    <Loading
      loading={inaccessibleUrlsQuery.isLoading || userQuery.isLoading}
      testId="account-settings-container"
    >
      {loadingStatus === 'none' ? (
        <>
          <Breadcrumbs />
          <div className={clsx(styles.wrapperContainer, styles.flexStart)}>
            <div className={clsx(styles.columnContainer, styles.flex1)}>
              <div className={clsx(styles.marginBottom24, styles.title)}>
                {i18n.t('personal_information')}
              </div>
              <div
                className={clsx(
                  styles.columnContainer,
                  styles.rowGap34,
                  styles.marginBottom42,
                )}
              >
                <div className={clsx(styles.columnContainer, styles.rowGap14)}>
                  <div className={styles.greyText}>{i18n.t('name')}</div>
                  <div className={styles.text}>
                    {userQuery.data?.firstName || '-'}
                  </div>
                </div>
                <div className={clsx(styles.columnContainer, styles.rowGap14)}>
                  <div className={styles.greyText}>{i18n.t('country')}</div>
                  <div className={styles.text}>{country?.label || '-'}</div>
                </div>
                <div className={clsx(styles.columnContainer, styles.rowGap14)}>
                  <div className={styles.greyText}>{i18n.t('email')}</div>
                  <div className={styles.text}>
                    {userQuery.data?.email || '-'}
                  </div>
                </div>
                <div className={clsx(styles.columnContainer, styles.rowGap14)}>
                  <div className={styles.greyText}>{i18n.t('role')}</div>
                  <div className={styles.text}>
                    {userQuery.data?.claimRoles.join(', ') || '-'}
                  </div>
                </div>
              </div>
              <div className={clsx(styles.rowContainer, styles.columnGap28)}>
                {!isAdmin && (
                  <Button
                    title={i18n.t('change_password')}
                    color={ButtonColor.Secondary}
                    onClick={() => {
                      trackEvent({
                        category: UserMenuCategory,
                        action: 'Open change password modal',
                        name: 'Open change password modal',
                      });
                      setIsChangePassModalShown(true);
                    }}
                  />
                )}
                <ActionText
                  text={i18n.t('logout')}
                  Svg={LogoutIcon}
                  className={styles.marginLeftMinus4}
                  onClickHandler={handleLogout}
                />
              </div>
            </div>
            <div className={styles.sectionDivider} />
            <div className={clsx(styles.columnContainer, styles.flex1)}>
              <div className={clsx(styles.marginBottom24, styles.title)}>
                {i18n.t('notifications')}
              </div>
              <div className={clsx(styles.columnContainer, styles.rowGap14)}>
                {isQualificationVisible && (
                  <div className={clsx(styles.columnContainer, styles.rowGap6)}>
                    <div
                      className={clsx(styles.rowContainer, styles.spaceBetween)}
                    >
                      <div
                        className={clsx(styles.subtitle, styles.paddingRight32)}
                      >
                        {i18n.t('qualifications')}
                      </div>
                      <Toggle
                        defaultChecked={!isQualificationNotificationsDisabled}
                        onChange={handleQualificationsToggleChange}
                      />
                    </div>
                    <div className={clsx(styles.text, styles.paddingRight64)}>
                      {i18n.t('qualifications_description')}
                    </div>
                    <div className={styles.rowDivider} />
                  </div>
                )}
                {/*{isFirmwareVisible && (*/}
                {/*  <div className={clsx(styles.columnContainer, styles.rowGap6)}>*/}
                {/*    <div*/}
                {/*      className={clsx(styles.rowContainer, styles.spaceBetween)}*/}
                {/*    >*/}
                {/*      <div*/}
                {/*        className={clsx(styles.subtitle, styles.paddingRight32)}*/}
                {/*      >*/}
                {/*        {i18n.t('firmware')}*/}
                {/*      </div>*/}
                {/*      <Toggle*/}
                {/*        defaultChecked={!isFirmwareNotificationsDisabled}*/}
                {/*        onChange={handleFirmwareToggleChange}*/}
                {/*      />*/}
                {/*    </div>*/}
                {/*    <div className={clsx(styles.text, styles.paddingRight64)}>*/}
                {/*      {i18n.t('firmware_description')}*/}
                {/*    </div>*/}
                {/*    <div className={styles.rowDivider} />*/}
                {/*  </div>*/}
                {/*)}*/}
                {isRepairsVisible && (
                  <div className={clsx(styles.columnContainer, styles.rowGap6)}>
                    <div
                      className={clsx(styles.rowContainer, styles.spaceBetween)}
                    >
                      <div
                        className={clsx(styles.subtitle, styles.paddingRight32)}
                      >
                        {i18n.t('repair_status_updates')}
                      </div>
                      <Toggle
                        defaultChecked={!isRepairsNotificationsDisabled}
                        testId="repairs-notifications-toggle"
                        onChange={handleRepairsToggleChange}
                      />
                    </div>
                    <div className={clsx(styles.text, styles.paddingRight64)}>
                      {i18n.t('repair_status_updates_description')}
                    </div>
                  </div>
                )}
                {isNotificationsHidden && (
                  <div
                    className={clsx(styles.columnContainer, styles.alignCenter)}
                  >
                    <NoNotificationsImage />
                    <div className={styles.noNotificationsMessage}>
                      {i18n.t('no_notification_settings_available')}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <LoadingPage
          loadingStatus={loadingStatus}
          titleLoadingContent={<div>{i18n.t('submit_loading_title')}</div>}
          titleContent={<div>{i18n.t('submit_completed_title')}</div>}
          bodyContent={<div>{i18n.t('submit_completed_text')}</div>}
          className={styles.errorContent}
        />
      )}
      {isChangePassModalShown && (
        <ChangePasswordModal
          closeModal={() => setIsChangePassModalShown(false)}
          changeLoadingStatus={changeLoadingStatus}
          loadingStatus={loadingStatus}
        />
      )}
    </Loading>
  );
};
